import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Users, User, Eye, Trash2, Link, CalendarDays, Tag, History } from 'lucide-react';
import { Event, EventStatus, getEffectiveEventStatus, EventHistoryEntry } from '../../types/event';
import Button from '../UI/Button';
import EventActions from './EventActions';
import { useAppSelector } from '../../store/hooks';
import HistoryModal from './HistoryModal';
import { eventAPI } from '../../services/eventAPI';
import { logError } from '../../utils/errorLogger';

interface EventCardProps {
  event: Event;
  onView?: (event: Event) => void;
  onEdit?: (event: Event) => void;
  onDelete?: (event: Event) => void;
  onManageGuests?: (event: Event) => void;
  onEventUpdate?: (updatedEvent: Event) => void;
  onApprove?: (event: Event) => void;
  onReject?: (event: Event) => void;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
  showActions?: boolean;
  compact?: boolean;
  usePhase2Actions?: boolean;
  userRole?: string;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onView,
  onEdit,
  onDelete,
  onManageGuests,
  onEventUpdate,
  onApprove,
  onReject,
  onError,
  onSuccess,
  showActions = true,
  compact = false,
  usePhase2Actions = false,
  userRole
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [history, setHistory] = useState<EventHistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Get the effective status (including pending approval logic)
  const effectiveStatus = getEffectiveEventStatus(event);

  // Fetch history when modal opens
  useEffect(() => {
    if (showHistoryModal && history.length === 0 && !loadingHistory) {
      setLoadingHistory(true);
      eventAPI.getEventHistory(event.id)
        .then(data => {
          setHistory(data);
          setLoadingHistory(false);
        })
        .catch(error => {
          logError('Error fetching event history', error);
          setLoadingHistory(false);
        });
    }
  }, [showHistoryModal, event.id, history.length, loadingHistory]);

  const getStatusBadge = (status: EventStatus) => {
    // Check if event has ended - if status is 'active' but event date has passed, show 'Ended'
    const eventHasEnded = isPast() && status === 'active';
    
    const statusConfig = {
      pending: {
        label: 'Pending Approval',
        className: theme === 'dark' ? 'bg-orange-900/30 text-orange-300 border border-orange-700/50' : 'bg-orange-100 text-orange-800',
        dotClassName: 'bg-orange-500'
      },
      active: {
        label: eventHasEnded ? 'Ended' : 'Active',
        className: eventHasEnded 
          ? (theme === 'dark' ? 'bg-gray-800/50 text-gray-300 border border-gray-600/50' : 'bg-gray-100 text-gray-800')
          : (theme === 'dark' ? 'bg-[#33FF00]/20 text-[#33FF00] border border-[#33FF00]/30' : 'bg-mirsal-green text-mirsal-black'),
        dotClassName: eventHasEnded ? 'bg-gray-500' : 'bg-mirsal-green'
      },
      rescheduled: {
        label: 'Rescheduled',
        className: theme === 'dark' ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700/50' : 'bg-yellow-100 text-yellow-800',
        dotClassName: 'bg-yellow-500'
      },
      completed: {
        label: 'Completed',
        className: theme === 'dark' ? 'bg-blue-900/30 text-blue-300 border border-blue-700/50' : 'bg-blue-100 text-blue-800',
        dotClassName: 'bg-blue-500'
      },
      canceled: {
        label: 'Cancelled',
        className: theme === 'dark' ? 'bg-red-900/30 text-red-300 border border-red-700/50' : 'bg-red-100 text-red-800',
        dotClassName: 'bg-red-500'
      },
      rejected: {
        label: 'Rejected',
        className: theme === 'dark' ? 'bg-red-900/40 text-red-300 border border-red-700/50' : 'bg-red-200 text-red-900',
        dotClassName: 'bg-red-600'
      },
      archived: {
        label: 'Archived',
        className: theme === 'dark' ? 'bg-gray-800/50 text-gray-300 border border-gray-600/50' : 'bg-gray-100 text-gray-800',
        dotClassName: 'bg-gray-500'
      }
    };

    const config = statusConfig[status];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${config.dotClassName}`}></span>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isUpcoming = () => {
    const eventDate = new Date(`${event.event_date}T${event.event_time}`);
    return eventDate > new Date();
  };

  const isPast = () => {
    const eventDate = new Date(`${event.event_date}T${event.event_time}`);
    return eventDate < new Date();
  };

  if (compact) {
    return (
      <div className={`rounded-lg border p-4 hover:shadow-md transition-all duration-200 h-full flex flex-col ${
        theme === 'dark' 
          ? 'bg-gray-900/80 border-gray-700/50 hover:border-[#33FF00]/30' 
          : 'bg-white border-gray-200 hover:border-[#33FF00]/20'
      }`}>
        <div className="flex items-start justify-between mb-3 flex-none">
          <div className="flex-1 min-w-0">
            <h3 className={`text-sm font-semibold mb-1 leading-tight h-8 overflow-hidden ${
              theme === 'dark' ? 'text-white' : 'text-mirsal-black'
            }`}>
              <span className="line-clamp-2">{event.title}</span>
            </h3>
            <div className="min-h-[16px]">
              {event.series_name && (
                <div className="flex items-center">
                  <CalendarDays className={`h-2 w-2 mr-1 flex-shrink-0 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-400'
                  }`} />
                  <span className={`text-xs truncate ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`}>{event.series_name}</span>
                </div>
              )}
            </div>
          </div>
          <div className="ml-2 flex-shrink-0 flex flex-col space-y-1">
            {getStatusBadge(effectiveStatus)}
            {event.series_name && (
              <span className={`text-xs px-1.5 py-0.5 rounded flex items-center ${
                theme === 'dark' 
                  ? 'text-blue-400 bg-blue-900/30 border border-blue-700/50' 
                  : 'text-blue-600 bg-blue-50'
              }`}>
                <CalendarDays className="h-2.5 w-2.5 mr-1" />
                Series
              </span>
            )}
          </div>
        </div>
        
        <div className="flex-1 space-y-2">
          <div className={`flex items-center text-xs ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <Calendar className="h-3 w-3 mr-2 flex-shrink-0" />
            <span className="truncate">{formatDate(event.event_date)}</span>
            <Clock className="h-3 w-3 ml-4 mr-2 flex-shrink-0" />
            <span>{formatTime(event.event_time)}</span>
          </div>
          
          <div className={`flex items-center text-xs ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <MapPin className="h-3 w-3 mr-2 flex-shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>

          {event.host_name && (
            <div className={`flex items-center text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <User className="h-3 w-3 mr-2 flex-shrink-0" />
              <span className="truncate">Hosted by {event.host_name}</span>
            </div>
          )}

          {(event.category || event.category_name) && (
            <div className={`flex items-center text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <Tag className="h-3 w-3 mr-2 flex-shrink-0" />
              <span className="truncate">{event.category?.name || event.category_name}</span>
            </div>
          )}

          <div className={`flex items-center text-xs ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <Users className="h-3 w-3 mr-2 flex-shrink-0" />
            <span>{event.accepted_count || 0}{event.guest_limit && `/${event.guest_limit}`} guests</span>
          </div>
        </div>
        
        {showActions && (
          <>
            {/* First row: View and Delete separated - Independent container */}
            <div className={`mt-3 pt-2 border-t flex-none ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
            }`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {onView && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onView(event)}
                      leftIcon={<Eye className="h-3 w-3" />}
                      className={`transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                          : '!text-black hover:!text-white hover:bg-gray-800'
                      }`}
                    >
                      View
                    </Button>
                  )}
                  
                  {onManageGuests && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onManageGuests(event)}
                      leftIcon={<Users className="h-3 w-3" />}
                      className={`transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                          : 'text-gray-900 hover:text-black hover:bg-gray-100'
                      }`}
                    >
                      Guests
                    </Button>
                  )}
                </div>

                {onDelete && !(userRole === 'host' && effectiveStatus === 'active') && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(event)}
                    leftIcon={<Trash2 className="h-3 w-3" />}
                    className={`transition-all duration-200 ${
                      theme === 'dark' 
                        ? 'text-red-400 hover:text-red-300 hover:bg-red-900/30' 
                        : '!text-black hover:!text-white hover:bg-red-600'
                    }`}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </div>
            
            {/* Second row: Approve and Reject separated - Completely independent container */}
            {(userRole === 'admin' && effectiveStatus === 'pending' && (onApprove || onReject)) && (
              <div className={`mt-0 pt-2 border-t flex-none ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <div className="flex items-center gap-2">
                  {onApprove && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onApprove(event)}
                      className={`flex-1 transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'bg-[#33FF00]/20 text-[#33FF00] border-[#33FF00]/30 hover:bg-[#33FF00]/30 hover:border-[#33FF00]/50' 
                          : 'bg-green-600 !text-white border-green-600 hover:bg-green-700 hover:border-green-700 hover:!text-white'
                      }`}
                    >
                      Approve
                    </Button>
                  )}
                  
                  {onReject && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReject(event)}
                      className={`flex-1 transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'bg-red-900/30 text-red-300 border-red-700/50 hover:bg-red-800/40 hover:border-red-600/60' 
                          : 'bg-red-600 !text-white border-red-600 hover:bg-red-700 hover:border-red-700 hover:!text-white'
                      }`}
                    >
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all duration-200 h-full flex flex-col ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border-gray-700/50 hover:border-[#33FF00]/30' 
        : 'bg-white border-gray-200 hover:border-[#33FF00]/20'
    }`}>
      {/* Header - Compact */}
      <div className="p-4 pb-3 flex-none">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold mb-1 leading-tight h-10 overflow-hidden ${
              theme === 'dark' ? 'text-white' : 'text-mirsal-black'
            }`}>
              <span className="line-clamp-2">{event.title}</span>
            </h3>
            <div className="flex items-center space-x-2 min-h-[16px]">
              {event.category && (
                <div className="flex items-center">
                  <Tag className={`h-3 w-3 mr-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                  }`} />
                  <span className={`text-xs truncate ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>{event.category.name}</span>
                </div>
              )}
              {event.series_name && (
                <div className="flex items-center">
                  <CalendarDays className={`h-3 w-3 mr-1 ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-400'
                  }`} />
                  <span className={`text-xs font-medium truncate ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`}>{event.series_name}</span>
                  {event.series_position && (
                    <span className={`text-xs ml-1 flex-shrink-0 ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                    }`}>
                      (#{event.series_position})
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1 ml-3 flex-shrink-0">
            {getStatusBadge(effectiveStatus)}
            {event.series_name && (
              <span className={`text-xs px-1.5 py-0.5 rounded flex items-center ${
                theme === 'dark' 
                  ? 'text-blue-400 bg-blue-900/30 border border-blue-700/50' 
                  : 'text-blue-600 bg-blue-50'
              }`}>
                <CalendarDays className="h-2.5 w-2.5 mr-1" />
                Series
              </span>
            )}
            {isPast() && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                theme === 'dark' 
                  ? 'text-gray-400 bg-gray-800/50 border border-gray-600/50' 
                  : 'text-gray-500 bg-gray-100'
              }`}>
                Past
              </span>
            )}
            {isUpcoming() && effectiveStatus === 'active' && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                theme === 'dark' 
                  ? 'text-[#33FF00] bg-[#33FF00]/10 border border-[#33FF00]/30' 
                  : 'text-mirsal-green bg-green-50'
              }`}>
                Upcoming
              </span>
            )}
          </div>
        </div>

        {/* Event Details - Compact Layout */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className={`flex items-center ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <Calendar className={`h-3 w-3 mr-1.5 flex-shrink-0 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`} />
            <span className="truncate">{formatDate(event.event_date)}</span>
          </div>
          
          <div className={`flex items-center ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <Clock className={`h-3 w-3 mr-1.5 flex-shrink-0 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`} />
            <span>{formatTime(event.event_time)}</span>
          </div>
          
          <div className={`flex items-center col-span-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <MapPin className={`h-3 w-3 mr-1.5 flex-shrink-0 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
            }`} />
            <span className="truncate">{event.location}</span>
          </div>
          
          {/* Host Name */}
          {event.host_name && (
            <div className={`flex items-center col-span-2 text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <User className={`h-3 w-3 mr-1.5 flex-shrink-0 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <span className="truncate">Hosted by {event.host_name}</span>
            </div>
          )}

          {/* Category */}
          {(event.category || event.category_name) && (
            <div className={`flex items-center col-span-2 text-xs ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <Tag className={`h-3 w-3 mr-1.5 flex-shrink-0 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`} />
              <span className="truncate">{event.category?.name || event.category_name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Content Area - Compact */}
      <div className="px-4 pb-2 flex-1 flex flex-col justify-between">
        {/* Guest Information */}
        <div className="flex-1 flex flex-col justify-center space-y-4">
          <div className={`flex items-center justify-between text-xs ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                <Users className={`h-3 w-3 mr-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                }`} />
                <span>
                  {event.accepted_count || 0}
                  {event.guest_limit && ` / ${event.guest_limit}`} guests
                </span>
              </div>
              
              {event.allow_companions && (
                <div className="flex items-center">
                  <User className={`h-3 w-3 mr-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
                  }`} />
                  <span>+1s</span>
                </div>
              )}
            </div>
            
            {event.pending_count && event.pending_count > 0 && (
              <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-yellow-400 bg-yellow-900/30 border border-yellow-700/50' 
                  : 'text-yellow-600 bg-yellow-50'
              }`}>
                {event.pending_count} pending
              </span>
            )}
          </div>

          {/* Compact Progress Bar */}
          {event.guest_limit ? (
            <div>
              <div className={`flex justify-between text-xs mb-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <span>Capacity</span>
                <span>{Math.round(((event.accepted_count || 0) / event.guest_limit) * 100)}%</span>
              </div>
              <div className={`w-full rounded-full h-1.5 ${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
              }`}>
                <div
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    event.is_full ? 'bg-red-500' : 'bg-mirsal-green'
                  }`}
                  style={{
                    width: `${Math.min(((event.accepted_count || 0) / event.guest_limit) * 100, 100)}%`
                  }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="h-4 flex items-center">
              <span className={`text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`}>No limit</span>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <>
          {usePhase2Actions && onEventUpdate && onError && onSuccess ? (
            // Phase 2 Enhanced Actions
            <div className={`px-4 py-3 border-t min-w-0 ${
              theme === 'dark' 
                ? 'bg-gray-800/50 border-gray-700' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="space-y-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    {onView && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onView(event)}
                        leftIcon={<Eye className="h-4 w-4" />}
                        className={`transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                            : '!text-black hover:!text-white hover:bg-gray-800'
                        }`}
                      >
                        View
                      </Button>
                    )}
                    
                    {onManageGuests && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onManageGuests(event)}
                        leftIcon={<Users className="h-4 w-4" />}
                        className={`transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                            : '!text-black hover:!text-white hover:bg-gray-800'
                        }`}
                      >
                        Guests
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 flex-wrap sm:justify-end">
                    {event.invitation_url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const fullUrl = `${window.location.origin}${event.invitation_url}`;
                          navigator.clipboard.writeText(fullUrl);
                          onSuccess?.('Invitation link copied!');
                        }}
                        leftIcon={<Link className="h-4 w-4" />}
                        className={`transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'text-[#33FF00] hover:text-[#2BD400] hover:bg-[#33FF00]/10' 
                            : 'text-mirsal-green hover:text-mirsal-green-dark hover:bg-green-50'
                        }`}
                      >
                        Copy Link
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowHistoryModal(true)}
                      leftIcon={<History className="h-4 w-4" />}
                      className={`transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                          : '!text-black hover:!text-white hover:bg-gray-800'
                      }`}
                    >
                      History
                    </Button>
                  </div>
                </div>
                
                {/* Phase 2 Action Buttons */}
                <EventActions
                  event={event}
                  onEventUpdate={onEventUpdate}
                  onEventDelete={(eventId) => onDelete?.(event)}
                  onError={onError}
                  onSuccess={onSuccess}
                />
              </div>
            </div>
          ) : (
            // Legacy Actions
            <>
              {/* First row: View and Delete separated - Independent container */}
              <div className={`px-4 py-3 border-t min-w-0 ${
                theme === 'dark' 
                  ? 'bg-gray-800/50 border-gray-700' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {onView && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onView(event)}
                        leftIcon={<Eye className="h-4 w-4" />}
                        className={`transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                            : '!text-black hover:!text-white hover:bg-gray-800'
                        }`}
                      >
                        View
                      </Button>
                    )}
                    
                    {onManageGuests && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onManageGuests(event)}
                        leftIcon={<Users className="h-4 w-4" />}
                        className={`transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'text-gray-300 hover:text-white hover:bg-gray-800' 
                            : 'text-gray-900 hover:text-black hover:bg-gray-100'
                        }`}
                      >
                        Guests
                      </Button>
                    )}
                  </div>

                  {onDelete && !(userRole === 'host' && effectiveStatus === 'active') && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDelete(event)}
                      leftIcon={<Trash2 className="h-4 w-4" />}
                      className={`transition-all duration-200 ${
                        theme === 'dark' 
                          ? 'text-red-400 hover:text-red-300 hover:bg-red-900/30' 
                          : '!text-black hover:!text-white hover:bg-red-600'
                      }`}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Second row: Approve and Reject separated - Completely independent container */}
              {/* Approve/Reject buttons for pending events (Admin only) - Hide if event has passed */}
              {(userRole === 'admin' && effectiveStatus === 'pending' && !isPast() && (onApprove || onReject)) && (
                <div className={`px-4 py-3 border-t min-w-0 ${
                  theme === 'dark' 
                    ? 'bg-gray-800/50 border-gray-700' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {onApprove && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onApprove(event)}
                        className={`flex-1 transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'bg-[#33FF00]/20 text-[#33FF00] border-[#33FF00]/30 hover:bg-[#33FF00]/30 hover:border-[#33FF00]/50' 
                            : 'bg-green-600 !text-white border-green-600 hover:bg-green-700 hover:border-green-700 hover:!text-white'
                        }`}
                      >
                        Approve
                      </Button>
                    )}
                    
                    {onReject && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onReject(event)}
                        className={`flex-1 transition-all duration-200 ${
                          theme === 'dark' 
                            ? 'bg-red-900/30 text-red-300 border-red-700/50 hover:bg-red-800/40 hover:border-red-600/60' 
                            : 'bg-red-600 !text-white border-red-600 hover:bg-red-700 hover:border-red-700 hover:!text-white'
                        }`}
                      >
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* History Modal */}
      <HistoryModal
        isOpen={showHistoryModal}
        onClose={() => {
          setShowHistoryModal(false);
        }}
        title={`Change History for ${event.title}`}
        history={history}
        loading={loadingHistory}
      />
    </div>
  );
};

export default EventCard;
